import { h, Fragment } from "preact"
import { useMemo, useCallback } from "preact/hooks"
import { toNumber } from "../helpers/productBadges"

export const useProductPricing = ({ product, activeVariant, scopedVariants, format }) => {
    const {
        formattedPrice,
        formattedCompareAtPrice,
        shouldShowCompareAt
    } = useMemo(() => {
        const variantPrice = toNumber(activeVariant?.price)
        const productPrice = toNumber(product?.price)
        const resolvedBasePrice = typeof variantPrice === "number"
            ? variantPrice
            : (typeof productPrice === "number" ? productPrice : 0)

        let minPrice = null
        let maxPrice = null
        const compareAtCandidates = []

        if (Array.isArray(scopedVariants) && scopedVariants.length > 0) {
            scopedVariants.forEach((variant) => {
                const variantSalePrice = toNumber(variant?.price)
                const variantCompareAtPrice = toNumber(variant?.listPrice)

                if (typeof variantSalePrice === "number") {
                    minPrice = minPrice === null ? variantSalePrice : Math.min(minPrice, variantSalePrice)
                    maxPrice = maxPrice === null ? variantSalePrice : Math.max(maxPrice, variantSalePrice)
                }

                if (
                    typeof variantCompareAtPrice === "number" &&
                    typeof variantSalePrice === "number" &&
                    variantCompareAtPrice > variantSalePrice
                ) {
                    compareAtCandidates.push(variantCompareAtPrice)
                }
            })
        }

        const resolvedMinPrice = typeof minPrice === "number" ? minPrice : resolvedBasePrice

        const fallbackVariantListPrice = toNumber(activeVariant?.listPrice)
        if (typeof fallbackVariantListPrice === "number") {
            const baselinePrice = typeof variantPrice === "number" ? variantPrice : resolvedBasePrice
            if (fallbackVariantListPrice > baselinePrice) {
                compareAtCandidates.push(fallbackVariantListPrice)
            }
        }

        const productListPrice = toNumber(product?.listPrice)
        if (typeof productListPrice === "number") {
            const productBaselinePrice = typeof productPrice === "number" ? productPrice : resolvedMinPrice
            if (productListPrice > productBaselinePrice) {
                compareAtCandidates.push(productListPrice)
            }
        }

        const highestCompareAtPrice = compareAtCandidates.length > 0
            ? Math.max(...compareAtCandidates)
            : null

        const resolvedMaxPrice = typeof maxPrice === "number" ? Math.max(maxPrice, resolvedMinPrice) : resolvedMinPrice
        const hasPriceRange = typeof resolvedMaxPrice === "number" && resolvedMaxPrice > resolvedMinPrice

        const trimTrailingZeroDecimals = (formattedValue) => {
            if (typeof formattedValue !== "string") return formattedValue
            return formattedValue.replace(/([.,])0+(?=\D|$)/g, "")
        }

        const formatPriceValue = (value) => {
            if (typeof value !== "number") return ""
            return trimTrailingZeroDecimals(format(value))
        }

        const formattedPriceValue = hasPriceRange
            ? `${formatPriceValue(resolvedMinPrice)} - ${formatPriceValue(resolvedMaxPrice)}`
            : formatPriceValue(resolvedMinPrice)

        const formattedCompareAtPriceValue = (
            typeof highestCompareAtPrice === "number" &&
            highestCompareAtPrice > resolvedMinPrice
        )
            ? formatPriceValue(highestCompareAtPrice)
            : null

        return {
            formattedPrice: formattedPriceValue,
            formattedCompareAtPrice: formattedCompareAtPriceValue,
            shouldShowCompareAt: Boolean(formattedCompareAtPriceValue)
        }
    }, [activeVariant, product, scopedVariants, format])

    const renderPrice = useCallback(() => {
        if (shouldShowCompareAt && formattedCompareAtPrice) {
            return h(
                Fragment,
                null,
                h("del", null, formattedCompareAtPrice),
                h("ins", null, formattedPrice)
            )
        }

        return h("span", null, formattedPrice)
    }, [shouldShowCompareAt, formattedCompareAtPrice, formattedPrice])

    return {
        formattedPrice,
        formattedCompareAtPrice,
        shouldShowCompareAt,
        renderPrice
    }
}
